import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  getUngroupedValues,
  getFilterSettings,
  type UngroupedValue
} from '../../api/server/groups';
import type { FilterField, MessageFilters } from './useMessageFilters';
import { logger } from '../../utils/logger';

const EMPTY_SELECTION_SENTINEL = '__empty_selection__';

export const UNGROUPED_TO_FILTER_FIELD: Record<string, FilterField> = {
  channels: 'channels',
  talkgroup_tags: 'talkgroupTags',
  radio_ids: 'radioIds',
  speakers: 'speakerIds',
  tags: 'tags'
};

interface UseGroupDataOptions {
  orgId: string | undefined;
  setField: (field: FilterField, values: string[]) => void;
  clearField: (field: FilterField) => void;
  filters?: MessageFilters;
}

interface UseGroupDataResult {
  ungroupedValues: UngroupedValue[];
  ungroupedType: string;
  selectedGroupFilterIds: string[] | null;
  handleGroupSelectionChange: (ids: string[] | null) => void;
}

export function useGroupData({
  orgId,
  setField,
  clearField,
  filters
}: UseGroupDataOptions): UseGroupDataResult {
  const [ungroupedValues, setUngroupedValues] = useState<UngroupedValue[]>([]);
  const [ungroupedType, setUngroupedType] = useState<string>('channels');
  const [selectedGroupFilterIds, setSelectedGroupFilterIds] = useState<string[] | null>(null);
  // Tracks the last value written by handleGroupSelectionChange so the sync
  // effect can distinguish "sentinel stripped by URL round-trip" from
  // "external code cleared the field."
  const lastLocalWriteRef = useRef<string[] | null | undefined>(undefined);

  useEffect(() => {
    if (!orgId) return;
    let cancelled = false;

    getUngroupedValues(orgId)
      .then((vals) => {
        if (!cancelled) setUngroupedValues(vals);
      })
      .catch((err) => logger.error('[useGroupData] Failed to load ungrouped values:', err));

    getFilterSettings(orgId)
      .then((settings) => {
        if (!cancelled) setUngroupedType(settings.default_group_type || 'channels');
      })
      .catch((err) => logger.error('[useGroupData] Failed to load group settings:', err));

    return () => {
      cancelled = true;
    };
  }, [orgId]);

  const groupFilterField = UNGROUPED_TO_FILTER_FIELD[ungroupedType];
  const externalEntry = filters && groupFilterField ? filters[groupFilterField] : undefined;
  const externalValues = externalEntry?.values;
  const externalMode = externalEntry?.mode;

  useEffect(() => {
    if (!externalValues) return;
    if (externalMode === 'exclude') {
      setSelectedGroupFilterIds(null);
      lastLocalWriteRef.current = undefined;
      return;
    }
    if (externalValues.length === 1 && externalValues[0] === EMPTY_SELECTION_SENTINEL) {
      setSelectedGroupFilterIds([]);
      return;
    }
    // Empty values after URL round-trip of the sentinel — don't snap back to "all"
    if (externalValues.length === 0 && lastLocalWriteRef.current !== undefined) {
      lastLocalWriteRef.current = undefined;
      return;
    }
    if (externalValues.length === 0) {
      setSelectedGroupFilterIds(null);
    } else {
      setSelectedGroupFilterIds(externalValues);
    }
  }, [externalValues, externalMode]);

  const handleGroupSelectionChange = useCallback(
    (ids: string[] | null) => {
      setSelectedGroupFilterIds(ids);

      const filterField = UNGROUPED_TO_FILTER_FIELD[ungroupedType];
      if (!filterField) return;

      if (ids === null) {
        lastLocalWriteRef.current = undefined;
        clearField(filterField);
      } else if (ids.length === 0) {
        lastLocalWriteRef.current = ids;
        setField(filterField, [EMPTY_SELECTION_SENTINEL]);
      } else {
        lastLocalWriteRef.current = undefined;
        setField(filterField, ids);
      }
    },
    [ungroupedType, clearField, setField]
  );

  return {
    ungroupedValues,
    ungroupedType,
    selectedGroupFilterIds,
    handleGroupSelectionChange
  };
}
